import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Shield, Mail, MailX, ArrowLeft, CreditCard, Lock, Bug } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { invalidateDashboardCache } from "@/lib/dashboardCache";
import { PRICING, subscriptionPriceLabel } from "@/lib/pricing";
import { toast } from "sonner";
import { LegalFooter } from "@/components/LegalLinks";

export default function Settings() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, refreshUser } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [budgetInput, setBudgetInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [cooldownInput, setCooldownInput] = useState("");
  const [savingCooldown, setSavingCooldown] = useState(false);

  const [cancelFlow, setCancelFlow] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelMessage, setCancelMessage] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);

  useEffect(() => {
    if (searchParams.get("gmail_connected") === "true") {
      toast.success("Gmail connected successfully");
      setSearchParams({}, { replace: true });
    } else if (searchParams.get("gmail_error")) {
      toast.error("Gmail connection failed. Please try again.");
      setSearchParams({}, { replace: true });
    }
    loadSettings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSettings = async () => {
    try {
      const res = await api.getSettings();
      setSettings(res.data);
      setBudgetInput(res.data.user.monthly_budget?.toString() || "500");
      setCooldownInput(res.data.user.cooldown_minutes?.toString() || "10");
    } catch (err) {
      if (err.response?.status === 401) navigate("/auth", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  const handleBudgetSave = async () => {
    setSaving(true);
    try {
      await api.updateBudget(parseFloat(budgetInput));
      toast.success("Budget updated");
      invalidateDashboardCache();
      await refreshUser();
      loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update budget");
    } finally {
      setSaving(false);
    }
  };

  const handleGmailConnect = async () => {
    try {
      const res = await api.connectGmail();
      if (res.data.connected) {
        toast.success("Gmail already connected");
        invalidateDashboardCache();
        loadSettings();
      } else if (res.data.auth_url) {
        window.location.href = res.data.auth_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to connect Gmail");
    }
  };

  const handleGmailDisconnect = async () => {
    try {
      await api.disconnectGmail();
      toast.success("Gmail disconnected");
      invalidateDashboardCache();
      loadSettings();
    } catch { toast.error("Failed to disconnect"); }
  };

  const handleClarityLock = async (enabled) => {
    try {
      await api.toggleClarityLock(enabled);
      toast.success(enabled ? "Clarity Lock activated" : "Clarity Lock deactivated");
      invalidateDashboardCache();
      loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  const handleCooldownSave = async () => {
    setSavingCooldown(true);
    try {
      const res = await api.updateCooldown(parseInt(cooldownInput, 10));
      setCooldownInput(res.data.cooldown_minutes.toString());
      toast.success("Pause timer updated");
      await refreshUser();
      loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update pause timer");
    } finally {
      setSavingCooldown(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    try {
      const res = await api.cancelFeedback({
        reason: cancelReason,
        message: cancelMessage || null,
        return_url: window.location.origin + "/settings",
      });
      if (res.data.action === "canceled") {
        // Cancelled during the free trial: ended right away, and they're back on the rest of their trial
        toast.success(res.data.plan === "trial"
          ? `Subscription canceled. You won't be charged, and you'll keep full access until ${trialEndLabel}.`
          : "Subscription canceled. You won't be charged.");
        setCancelFlow(false);
        setCancelReason("");
        invalidateDashboardCache();
        await refreshUser();
        loadSettings();
      } else if (res.data.portal_url) {
        window.location.href = res.data.portal_url;
      }
    } catch {
      toast.error("Failed to process cancellation");
    } finally {
      setCancelLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Shield className="w-8 h-8 text-navy animate-pulse" />
      </div>
    );
  }

  const u = settings?.user;
  const gmail = settings?.gmail;
  const sub = settings?.subscription;
  const hasAccess = u?.plan === "premium" || (u?.plan === "trial" && u?.trial_end_date && new Date(u.trial_end_date) > new Date());
  // Subscribed during the free trial: Stripe hasn't charged yet
  const trialingSubscriber = u?.plan === "premium" && u?.trial_end_date && new Date(u.trial_end_date) > new Date();
  const trialEndLabel = trialingSubscriber ? new Date(u.trial_end_date).toLocaleDateString(undefined, { month: "long", day: "numeric" }) : "";
  // The rate Stripe actually bills them (not today's list price), and the end date if they've already cancelled in the portal
  const subPrice = subscriptionPriceLabel(sub);
  const subEndsLabel = sub?.cancel_at ? new Date(sub.cancel_at * 1000).toLocaleDateString(undefined, { month: "long", day: "numeric" }) : "";

  return (
    <div className="min-h-screen bg-background" data-testid="settings-page">
      <header className="bg-white border-b border-border">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
          </Button>
          <Separator orientation="vertical" className="h-6" />
          <h1 className="text-lg font-semibold text-navy font-display">Settings</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        {/* Account */}
        <Card className="border-border shadow-sm" data-testid="account-card">
          <CardHeader>
            <CardTitle className="text-base font-semibold text-gray-900 font-display">Account</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center text-lg font-medium text-gray-600">
                {u?.name?.[0]?.toUpperCase() || "U"}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{u?.name}</p>
                <p className="text-sm text-gray-500">{u?.email}</p>
              </div>
              <Badge variant="outline" className="ml-auto text-xs uppercase">
                {u?.plan || "free"} Plan
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Budget */}
        <Card className="border-border shadow-sm" data-testid="budget-settings-card">
          <CardHeader>
            <CardTitle className="text-base font-semibold text-gray-900 font-display">Monthly Budget</CardTitle>
            <CardDescription className="text-sm text-gray-500">Your monthly spending intention.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Label className="text-sm text-gray-700">Budget Amount ($)</Label>
                <Input
                  type="number"
                  min="1"
                  step="1"
                  className="mt-1.5 h-11 border-border"
                  value={budgetInput}
                  onChange={(e) => setBudgetInput(e.target.value)}
                  disabled={u?.clarity_lock}
                  data-testid="budget-input"
                />
              </div>
              <Button
                className="h-11 px-6 bg-navy hover:bg-navy-hover active:bg-navy-active text-white font-medium text-sm"
                onClick={handleBudgetSave}
                disabled={saving || u?.clarity_lock}
                data-testid="save-budget-btn"
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
            {u?.clarity_lock && (
              <p className="mt-3 text-xs text-reflection-foreground flex items-center gap-1.5">
                <Lock className="w-3.5 h-3.5" /> Clarity Lock is active — budget editing is locked.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Full-access features */}
        <Card className="border-border shadow-sm" data-testid="premium-features-card">
          <CardHeader>
            <CardTitle className="text-base font-semibold text-gray-900 font-display">
              {u?.plan === "premium" ? "Premium Features" : hasAccess ? "Trial Features" : u?.plan === "trial" ? "Trial Ended" : "Premium Features"}
            </CardTitle>
            {(u?.plan === "trial" || u?.plan === "free") && (
              <CardDescription className="text-sm text-gray-500">
                {hasAccess ? "Full access during your trial" : u?.plan === "free" ? "Unlock Gmail import, Clarity Lock, and more" : "Subscribe to continue using CartControl"}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Gmail Connection */}
            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium text-gray-900">Gmail Connection</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Premium backfill — the extension tracks your purchases automatically; Gmail catches the ones it can't see. Read-only, and we never store email content.
                </p>
              </div>
              {gmail?.connected ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-sage/10 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-sage" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{gmail.email || u?.email}</p>
                      <p className="text-xs text-gray-400">
                        {gmail.last_sync ? `Last imported: ${new Date(gmail.last_sync).toLocaleString()}` : "Nothing imported yet"}
                      </p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" className="border-border text-gray-600" onClick={handleGmailDisconnect} data-testid="disconnect-gmail-btn">
                    <MailX className="w-4 h-4 mr-1.5" /> Disconnect
                  </Button>
                </div>
              ) : hasAccess ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Your extension already tracks purchases you make in this browser. Connecting Gmail is an optional backfill that catches what it can't see:
                    </p>
                    <ul className="space-y-1.5 text-sm text-gray-600">
                      <li className="flex items-start gap-2"><span className="text-sage">•</span> Purchases made on your phone or other devices</li>
                      <li className="flex items-start gap-2"><span className="text-sage">•</span> Purchases made in other browsers</li>
                      <li className="flex items-start gap-2"><span className="text-sage">•</span> Anything you bought before installing CartControl</li>
                    </ul>
                    <p className="text-xs text-gray-400 leading-relaxed">
                      We only save order numbers, amounts, dates, and retailer names — never email content or personal messages.{" "}
                      <a href="https://cartcontrol.io/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600 transition-colors">Privacy Policy</a>
                    </p>
                  </div>
                  <Button className="bg-navy hover:bg-navy-hover active:bg-navy-active text-white font-medium text-sm h-11 px-6" onClick={handleGmailConnect} data-testid="connect-gmail-btn">
                    <Mail className="w-4 h-4 mr-2" /> Connect Gmail
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-3.5 rounded-lg bg-reflection border border-reflection-border">
                    <Lock className="w-4 h-4 text-reflection-foreground shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-reflection-foreground">Gmail import is a Premium feature</p>
                      <p className="text-xs text-reflection-foreground mt-0.5 leading-relaxed opacity-80">
                        On Basic, your Amazon purchases are tracked automatically by the extension. Upgrade to backfill purchases from your phone, other browsers, and from before you installed CartControl.
                      </p>
                    </div>
                  </div>
                  <Button className="bg-navy hover:bg-navy-hover active:bg-navy-active text-white font-medium text-sm h-11 px-6" onClick={() => navigate("/pricing")} data-testid="gmail-upgrade-btn">
                    <CreditCard className="w-4 h-4 mr-2" /> Upgrade to Premium
                  </Button>
                </div>
              )}
            </div>
            <Separator />
            <div className={`flex items-center justify-between ${!hasAccess ? "opacity-60" : ""}`}>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900">Clarity Lock</p>
                  {!hasAccess && (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 leading-4 border-reflection-border text-reflection-foreground bg-reflection">
                      Premium
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">Stay intentional — locks budget changes once set</p>
              </div>
              <Switch
                checked={hasAccess && (u?.clarity_lock || false)}
                onCheckedChange={handleClarityLock}
                disabled={!hasAccess}
                data-testid="clarity-lock-switch"
              />
            </div>
            <Separator />
            <div className={`flex items-center justify-between gap-4 ${!hasAccess ? "opacity-60" : ""}`}>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900">Pause Timer Length</p>
                  {!hasAccess && (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 leading-4 border-reflection-border text-reflection-foreground bg-reflection">
                      Premium
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">How long the cooldown timer runs before checkout, in minutes</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Input
                  type="number"
                  min="1"
                  max="60"
                  step="1"
                  className="w-20 h-9 border-border text-sm"
                  value={cooldownInput}
                  onChange={(e) => setCooldownInput(e.target.value)}
                  disabled={!hasAccess}
                  data-testid="cooldown-input"
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="border-border text-gray-600 text-xs h-9"
                  onClick={handleCooldownSave}
                  disabled={!hasAccess || savingCooldown}
                  data-testid="save-cooldown-btn"
                >
                  {savingCooldown ? "Saving..." : "Save"}
                </Button>
              </div>
            </div>
            <Separator />
            {u?.plan === "premium" ? (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">Subscription</p>
                  <p className="text-xs text-gray-500 mt-0.5" data-testid="subscription-summary">
                    {subEndsLabel
                      ? `Premium until ${subEndsLabel} — your subscription won't renew`
                      : trialingSubscriber
                        ? `Premium — ${subPrice || "billing starts"} after your free trial`
                        : `Premium — ${subPrice ? `${subPrice}, ` : ""}auto-renewal`}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-border text-gray-600 text-xs"
                    onClick={async () => {
                      try {
                        const res = await api.createPortalSession();
                        window.location.href = res.data.url;
                      } catch {
                        toast.error("Failed to open billing portal");
                      }
                    }}
                    data-testid="manage-billing-btn"
                  >
                    Manage Billing
                  </Button>
                  {/* Already cancelled: nothing left to cancel. Manage Billing can still resume it. */}
                  {!subEndsLabel && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-gray-400 hover:text-danger text-xs"
                      onClick={() => setCancelFlow(true)}
                      data-testid="cancel-subscription-btn"
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">Subscribe</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {u?.plan === "free"
                      ? `Unlock Gmail import, Clarity Lock, and more — ${PRICING.priceWithInterval}`
                      : `Keep your access after trial ends — ${PRICING.priceWithInterval}`}
                  </p>
                </div>
                <Button size="sm" className="bg-navy hover:bg-navy-hover active:bg-navy-active text-white text-xs" onClick={() => navigate("/pricing")} data-testid="settings-subscribe-btn">
                  Subscribe Now
                </Button>
              </div>
            )}

            {/* Cancellation feedback flow */}
            {cancelFlow && (
              <div className="bg-muted rounded-lg p-4 space-y-3 border border-border" data-testid="cancel-flow">
                <p className="text-sm font-medium text-gray-700">Why are you canceling?</p>
                {trialingSubscriber && (
                  <p className="text-xs text-gray-500" data-testid="cancel-trial-note">
                    You're still in your free trial and haven't been charged. If you cancel, you'll keep full access until {trialEndLabel}.
                  </p>
                )}
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: "too_expensive", label: "Too expensive" },
                    { value: "not_useful", label: "Not useful enough" },
                    { value: "not_working", label: "Didn't work properly" },
                    { value: "trying_it", label: "Just trying it out" },
                    { value: "other", label: "Other" },
                  ].map((opt) => (
                    <Button
                      key={opt.value}
                      variant={cancelReason === opt.value ? "default" : "outline"}
                      size="sm"
                      className={`text-xs h-8 ${cancelReason === opt.value ? "bg-navy text-white" : "border-border text-gray-600"}`}
                      onClick={() => setCancelReason(opt.value)}
                      data-testid={`cancel-reason-${opt.value}`}
                    >
                      {opt.label}
                    </Button>
                  ))}
                </div>
                {cancelReason === "other" && (
                  <Input
                    placeholder="Tell us more..."
                    value={cancelMessage}
                    onChange={(e) => setCancelMessage(e.target.value)}
                    className="text-sm h-9 border-border"
                    data-testid="cancel-message-input"
                  />
                )}
                <div className="flex gap-2 pt-1">
                  <Button
                    size="sm"
                    className="bg-danger hover:bg-danger/90 text-white text-xs h-8"
                    disabled={!cancelReason || cancelLoading}
                    onClick={handleCancelSubscription}
                    data-testid="confirm-cancel-btn"
                  >
                    {cancelLoading ? "Processing..." : trialingSubscriber ? "Cancel Subscription" : "Continue Cancellation"}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs h-8 text-gray-500"
                    onClick={() => { setCancelFlow(false); setCancelReason(""); }}
                    data-testid="cancel-cancel-btn"
                  >
                    Never mind
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Support */}
        <Card className="border-border shadow-sm" data-testid="support-card">
          <CardHeader>
            <CardTitle className="text-base font-semibold text-gray-900 font-display">Support</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">Found a bug?</p>
                <p className="text-xs text-gray-500 mt-0.5">Let us know and we'll take a look.</p>
              </div>
              <Button variant="outline" size="sm" className="border-border text-gray-600" asChild data-testid="report-bug-btn">
                <a href="mailto:hello@cartcontrol.io?subject=Bug%20Report%20-%20CartControl">
                  <Bug className="w-4 h-4 mr-1.5" /> Report a Bug
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Legal */}
        <div className="text-center pt-2 pb-4">
          <LegalFooter />
        </div>
      </main>
    </div>
  );
}
